import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges
} from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { TranslateService } from '@ngx-translate/core';

/**
 * Ensures the inner combobox input of ng-select has an accessible name (WCAG 3.3.2, 2.5.3).
 * Prefer {@link ngSelectA11yLabelledBy} so the visible &lt;label&gt; text is the accessible name.
 *
 * ng-select puts role="combobox" on the .ng-input wrapper while focus is on the inner input;
 * Deque manual audits flag the input as missing a role. We move combobox-related ARIA from the
 * wrapper onto the focused input after each sync.
 *
 * Applied to every {@code ng-select} so clear (×) is keyboard-operable and combobox role stays
 * on the focused input even when callers omit {@code appNgSelectA11y} (WCAG 2.1.1).
 *
 * Mutation observation is intentionally shallow (no full subtree): attribute ping-pong on role/aria
 * between this directive and ng-select caused infinite MutationObserver loops. Dropdown panels
 * appended under {@code body} do not bubble to the host observer.
 */
// Keep element selector so a11y fixes apply to every ng-select instance.
// eslint-disable-next-line @angular-eslint/directive-selector
@Directive({ selector: 'ng-select' })
export class NgSelectA11yDirective implements AfterViewInit, OnChanges, OnDestroy {
  private static readonly LOADING_A11Y_DELAY_MS = 120;
  /** Fallback accessible name when no labelledby id (e.g. translated label text). */
  @Input() appNgSelectA11y: string | undefined;
  /** ID for the inner input (must match &lt;label for="..."&gt;). */
  @Input() ngSelectA11yId: string | undefined;
  /** WCAG 2.5.3 — id of the visible label element (aria-labelledby). */
  @Input() ngSelectA11yLabelledBy: string | undefined;
  /** WCAG 3.3.1 — id of the validation message element (e.g. app-form-error). */
  @Input() ngSelectA11yDescribedBy: string | null | undefined;
  /** WCAG 3.3.1 — expose invalid state on the combobox search input. */
  @Input() ngSelectA11yInvalid: boolean | undefined;
  /** WCAG 4.1.2 / 1.4.11 — clear control: aria-label + remove erroneous aria-hidden on × (Deque manual audit). */
  @Input() ngSelectClearAriaLabel: string | undefined;

  /** Host: direct childList + class (open/closed). No subtree — avoids deep aria churn. */
  private hostObserver?: MutationObserver;
  /** `.ng-select-container` direct childList only (structure without full subtree). */
  private containerObserver?: MutationObserver;
  /** Tracks ng-select `loading` spinner presence for WCAG 4.1.3 (avoid duplicate LiveAnnouncer calls). */
  private lastLoadingSpinnerPresent: boolean | undefined;
  /** Tracks last "no results" text to avoid repeated announcements (WCAG 4.1.3). */
  private lastNotFoundText: string | undefined;
  /** Persistent polite region on the host (see {@link pokeComboboxStatusMessage}). */
  private comboboxStatusRegion?: HTMLElement;

  /** True while {@link runApplyA11yCore} runs; observers are detached so our DOM work does not retrigger. */
  private isPatching = false;
  /** Another schedule was requested while patching; run once after reconnect. */
  private deferredApplyAfterPatch = false;
  /** Coalesce multiple schedules to one rAF. */
  private scheduleApplyRafId: number | null = null;
  /** Cancel reconnect rAF on destroy. */
  private reconnectObserversRafId: number | null = null;
  /** focusin fallback when deep DOM updates do not change host direct children. */
  private focusInUnlisten?: () => void;
  /** Re-sync aria-valuetext when the bound model changes (WCAG 4.1.2 — collapsed combobox must not read "blank"). */
  private changeUnlisten?: () => void;
  /** Cleared on destroy and when the loading spinner is removed (WCAG 4.1.3 / stale LiveAnnouncer). */
  private loadingA11yTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private el: ElementRef<HTMLElement>,
    private translate: TranslateService,
    private renderer: Renderer2,
    private liveAnnouncer: LiveAnnouncer
  ) {}

  ngAfterViewInit(): void {
    this.attachObservers();
    this.focusInUnlisten = this.renderer.listen(this.el.nativeElement, 'focusin', () => {
      if (!this.isPatching) {
        this.scheduleApply();
      }
    });
    this.changeUnlisten = this.renderer.listen(this.el.nativeElement, 'change', () => {
      if (!this.isPatching) {
        this.scheduleApply();
      }
    });
    this.scheduleApply();
  }

  ngOnDestroy(): void {
    this.clearLoadingA11yTimeout();
    if (this.reconnectObserversRafId != null) {
      cancelAnimationFrame(this.reconnectObserversRafId);
      this.reconnectObserversRafId = null;
    }
    if (this.scheduleApplyRafId != null) {
      cancelAnimationFrame(this.scheduleApplyRafId);
      this.scheduleApplyRafId = null;
    }
    this.hostObserver?.disconnect();
    this.containerObserver?.disconnect();
    this.focusInUnlisten?.();
    this.changeUnlisten?.();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['appNgSelectA11y'] ||
      changes['ngSelectA11yId'] ||
      changes['ngSelectA11yDescribedBy'] ||
      changes['ngSelectA11yLabelledBy'] ||
      changes['ngSelectA11yInvalid'] ||
      changes['ngSelectClearAriaLabel']
    ) {
      this.scheduleApply();
    }
  }

  /**
   * Debounced to one animation frame; defers if a patch is in progress so ng-select synchronous
   * re-renders after attribute changes do not recurse before observers are detached.
   */
  private scheduleApply(): void {
    if (this.isPatching) {
      this.deferredApplyAfterPatch = true;
      return;
    }
    if (this.scheduleApplyRafId != null) {
      return;
    }
    this.scheduleApplyRafId = requestAnimationFrame(() => {
      this.scheduleApplyRafId = null;
      this.applyA11y();
    });
  }

  private detachObservers(): void {
    this.hostObserver?.disconnect();
    this.containerObserver?.disconnect();
    if (this.reconnectObserversRafId != null) {
      cancelAnimationFrame(this.reconnectObserversRafId);
      this.reconnectObserversRafId = null;
    }
  }

  private attachObservers(): void {
    const host = this.el.nativeElement;

    this.hostObserver?.disconnect();
    this.hostObserver = new MutationObserver((records) => {
      if (this.isPatching) {
        return;
      }
      if (!this.shouldScheduleFromHostMutationRecords(records)) {
        return;
      }
      this.scheduleApply();
    });
    this.hostObserver.observe(host, {
      childList: true,
      subtree: false,
      attributes: true,
      attributeFilter: ['class']
    });

    this.containerObserver?.disconnect();
    const container = host.querySelector('.ng-select-container');
    if (container) {
      this.containerObserver = new MutationObserver((records) => {
        if (this.isPatching) {
          return;
        }
        if (!this.shouldScheduleFromContainerMutationRecords(records)) {
          return;
        }
        this.scheduleApply();
      });
      this.containerObserver.observe(container, {
        childList: true,
        subtree: false
      });
    }
  }

  /**
   * Ignore our own live-region insertion; react to host class (e.g. ng-select-opened) and other
   * direct child changes.
   */
  private shouldScheduleFromHostMutationRecords(records: MutationRecord[]): boolean {
    for (const m of records) {
      if (m.type === 'childList') {
        if (this.isOnlyComboboxStatusInsertion(m)) {
          continue;
        }
        if (m.addedNodes.length > 0 || m.removedNodes.length > 0) {
          return true;
        }
      }
      if (m.type === 'attributes' && m.attributeName === 'class') {
        return true;
      }
    }
    return false;
  }

  private isOnlyComboboxStatusInsertion(m: MutationRecord): boolean {
    if (m.type !== 'childList' || m.target !== this.el.nativeElement) {
      return false;
    }
    if (m.addedNodes.length === 1 && m.removedNodes.length === 0) {
      return m.addedNodes[0] === this.comboboxStatusRegion;
    }
    return false;
  }

  /** Structural changes to the container’s direct children (e.g. value row updates). */
  private shouldScheduleFromContainerMutationRecords(records: MutationRecord[]): boolean {
    return records.some(
      (m) => m.type === 'childList' && (m.addedNodes.length > 0 || m.removedNodes.length > 0)
    );
  }

  private applyA11y(): void {
    if (this.isPatching) {
      this.deferredApplyAfterPatch = true;
      return;
    }
    this.isPatching = true;
    this.detachObservers();

    try {
      this.runApplyA11yCore();
    } finally {
      this.reconnectObserversRafId = requestAnimationFrame(() => {
        this.reconnectObserversRafId = null;
        this.isPatching = false;
        this.attachObservers();
        if (this.deferredApplyAfterPatch) {
          this.deferredApplyAfterPatch = false;
          this.scheduleApply();
        }
      });
    }
  }

  private runApplyA11yCore(): void {
    const input = this.el.nativeElement.querySelector('.ng-input input') as HTMLInputElement | null;
    if (!input) {
      return;
    }
    this.moveComboboxRoleToInput(input);
    if (this.ngSelectA11yId) {
      input.id = this.ngSelectA11yId;
    }
    if (this.ngSelectA11yLabelledBy) {
      input.setAttribute('aria-labelledby', this.ngSelectA11yLabelledBy);
      input.removeAttribute('aria-label');
    } else if (this.appNgSelectA11y) {
      input.setAttribute('aria-label', this.appNgSelectA11y);
      input.removeAttribute('aria-labelledby');
    } else {
      const fallback = this.getFallbackComboboxAccessibleName(input);
      if (fallback) {
        input.setAttribute('aria-label', fallback);
        input.removeAttribute('aria-labelledby');
      }
    }
    input.setAttribute('autocomplete', 'off');
    if (this.ngSelectA11yDescribedBy) {
      input.setAttribute('aria-describedby', this.ngSelectA11yDescribedBy);
    } else {
      input.removeAttribute('aria-describedby');
    }
    if (this.ngSelectA11yInvalid === true) {
      input.setAttribute('aria-invalid', 'true');
    } else {
      input.removeAttribute('aria-invalid');
    }
    this.syncSelectedValueToCombobox(input);
    this.fixClearButton();
    this.syncLoadingStateForScreenReaders(input);
    this.fixDropdownPanelA11y();
  }

  /**
   * WCAG 4.1.2 — ng-select shows the chosen label in `.ng-value-label` while the combobox input stays empty;
   * expose the visible selection via {@code aria-valuetext} so NVDA does not announce "blank" when collapsed.
   */
  private syncSelectedValueToCombobox(input: HTMLInputElement): void {
    const valueLabel = this.el.nativeElement.querySelector('.ng-value-label')?.textContent?.trim();
    if (valueLabel) {
      input.setAttribute('aria-valuetext', valueLabel);
    } else {
      input.removeAttribute('aria-valuetext');
    }
  }

  /**
   * WCAG 4.1.3 — async/typeahead loading: ng-select shows `.ng-spinner-loader` + dropdown "Loading...".
   * `aria-busy` exposes busy state; {@link pokeComboboxStatusMessage} updates the host live region once when loading starts.
   */
  private syncLoadingStateForScreenReaders(input: HTMLInputElement): void {
    const spinnerPresent = !!this.el.nativeElement.querySelector('.ng-spinner-loader');
    if (spinnerPresent) {
      input.setAttribute('aria-busy', 'true');
    } else {
      input.removeAttribute('aria-busy');
    }
    if (!spinnerPresent) {
      this.clearLoadingA11yTimeout();
    } else if (this.lastLoadingSpinnerPresent !== true) {
      this.clearLoadingA11yTimeout();
      this.loadingA11yTimeoutId = setTimeout(() => {
        this.loadingA11yTimeoutId = null;
        this.liveAnnouncer.announce(
          this.translate.instant('A11Y_COMBOBOX_LOADING'),
          'polite'
        );
      }, NgSelectA11yDirective.LOADING_A11Y_DELAY_MS);
    }
    this.lastLoadingSpinnerPresent = spinnerPresent;
  }

  private clearLoadingA11yTimeout(): void {
    if (this.loadingA11yTimeoutId != null) {
      clearTimeout(this.loadingA11yTimeoutId);
      this.loadingA11yTimeoutId = null;
    }
  }

  /**
   * ng-select: (1) host {@code ng-dropdown-panel} + {@code .ng-dropdown-panel-items} both used role=listbox;
   * (2) Axe aria-required-children — options are projected into the inner {@code > div} (sibling of padding),
   * not direct children of {@code .scroll-host}, so the scroll host must not be the listbox. (3) Move id to the
   * inner content wrapper; keep outer scroll host focusable when overflow (scrollable-region-focusable).
   */
  private fixDropdownPanelA11y(): void {
    const input = this.el.nativeElement.querySelector('.ng-input input') as HTMLInputElement | null;
    if (!input) {
      return;
    }
    const owns = input.getAttribute('aria-owns');
    if (!owns) {
      return;
    }

    let panelHost = document.getElementById(owns) as HTMLElement | null;
    if (panelHost && !panelHost.classList.contains('ng-dropdown-panel')) {
      panelHost = panelHost.closest('ng-dropdown-panel') as HTMLElement | null;
    }
    if (!panelHost?.classList.contains('ng-dropdown-panel')) {
      panelHost = document.querySelector(`ng-dropdown-panel#${CSS.escape(owns)}`) as HTMLElement | null;
    }
    if (!panelHost?.classList.contains('ng-dropdown-panel')) {
      return;
    }

    const itemsEl = panelHost.querySelector('.ng-dropdown-panel-items') as HTMLElement | null;
    if (!itemsEl) {
      return;
    }

    // Inner div wraps ng-content; ng-container does not appear in DOM — .ng-option are direct children here.
    const contentEl =
      (itemsEl.querySelector(':scope > div:nth-child(2)') as HTMLElement | null) ??
      (itemsEl.children[1] as HTMLElement | undefined) ??
      null;
    if (!contentEl) {
      return;
    }

    const multiselect = panelHost.getAttribute('aria-multiselectable');
    panelHost.setAttribute('role', 'presentation');
    panelHost.removeAttribute('aria-label');
    panelHost.removeAttribute('aria-multiselectable');

    itemsEl.setAttribute('role', 'presentation');
    itemsEl.removeAttribute('aria-label');
    itemsEl.removeAttribute('aria-multiselectable');

    if (panelHost.id === owns) {
      panelHost.removeAttribute('id');
    }
    if (itemsEl.id === owns) {
      itemsEl.removeAttribute('id');
    }
    contentEl.id = owns;

    contentEl.setAttribute('role', 'listbox');
    const accName =
      input.getAttribute('aria-label')?.trim() ||
      this.resolveAriaLabelledByText(input.getAttribute('aria-labelledby'));
    if (accName) {
      contentEl.setAttribute('aria-label', accName);
    }
    if (multiselect) {
      contentEl.setAttribute('aria-multiselectable', multiselect);
    }

    if (itemsEl.scrollHeight > itemsEl.clientHeight + 2) {
      itemsEl.setAttribute('tabindex', '0');
    } else {
      itemsEl.removeAttribute('tabindex');
    }

    this.ensureNgOptionRoles(contentEl);
    // Options render after CD / async items (e.g. Owner/Flex period lists); re-run for Axe required-children.
    const listboxRef = contentEl;
    requestAnimationFrame(() => this.ensureNgOptionRoles(listboxRef));
    setTimeout(() => this.ensureNgOptionRoles(listboxRef), 0);
    setTimeout(() => this.ensureNgOptionRoles(listboxRef), 50);

    this.announceNotFoundIfVisible(panelHost);
  }

  /**
   * WCAG 4.1.3 — announce "No items found" (ng-select notFoundText) once per unique message so screen
   * readers know the dropdown has no matching results (Deque: status-msg-not-auto-announced).
   *
   * When a custom {@code ng-notfound-tmp} template already carries {@code role="alert"} on an inner
   * element, the browser self-announces on insertion — skip {@link pokeComboboxStatusMessage} to
   * prevent the duplicate "No items found. No items found." heard in NVDA.
   */
  private announceNotFoundIfVisible(panelHost: HTMLElement): void {
    const notFoundEl = panelHost.querySelector('.ng-notfound') as HTMLElement | null;
    const text = notFoundEl?.textContent?.trim();
    if (!text || text === this.lastNotFoundText) {
      if (!text) {
        this.lastNotFoundText = undefined;
      }
      return;
    }
    this.lastNotFoundText = text;
    // Custom ng-notfound-tmp uses role="alert" which self-announces on DOM insertion.
    // pokeComboboxStatusMessage is only needed for plain ng-select without a custom template.
    const hasSelfAnnouncingEl = !!notFoundEl?.querySelector('[role="alert"]');
    if (hasSelfAnnouncingEl) {
      return;
    }
    queueMicrotask(() => this.pokeComboboxStatusMessage(text));
  }

  /**
   * WCAG 4.1.3 — always-mounted {@code role="status"} on the ng-select host; clear then set so updates are spoken.
   */
  private pokeComboboxStatusMessage(message: string): void {
    const host = this.el.nativeElement;
    if (!this.comboboxStatusRegion || !host.contains(this.comboboxStatusRegion)) {
      const div = this.renderer.createElement('div') as HTMLElement;
      this.renderer.addClass(div, 'cdk-visually-hidden');
      this.renderer.setAttribute(div, 'role', 'status');
      this.renderer.setAttribute(div, 'aria-live', 'polite');
      this.renderer.setAttribute(div, 'aria-atomic', 'true');
      this.renderer.appendChild(host, div);
      this.comboboxStatusRegion = div;
    }
    this.renderer.setProperty(this.comboboxStatusRegion, 'textContent', '');
    queueMicrotask(() => {
      this.renderer.setProperty(this.comboboxStatusRegion, 'textContent', message);
    });
  }

  /**
   * Axe aria-required-children: {@code role="listbox"} must own {@code role="option"} or {@code group} nodes.
   * ng-select’s loading / not-found rows use {@code .ng-option} without {@code [attr.role]}; virtual scroll
   * can also nest options so {@code :scope >} misses them — scan all {@code .ng-option} under the listbox.
   *
   * WCAG 4.1.3 — custom {@code ng-notfound-tmp} templates may carry {@code role="alert"} to self-announce
   * "No items found". Never overwrite a live-region role ({@code alert} / {@code status}) with
   * {@code option}: doing so silently removes the announcement mechanism and is the root cause of the
   * Deque 2157384 / 2162253 "Not Fixed" findings.
   */
  private ensureNgOptionRoles(listboxEl: HTMLElement): void {
    listboxEl.querySelectorAll('.ng-option').forEach((node) => {
      const el = node as HTMLElement;
      if (!listboxEl.contains(el)) {
        return;
      }
      if (el.classList.contains('ng-optgroup')) {
        el.setAttribute('role', 'group');
        return;
      }
      const existing = el.getAttribute('role');
      // Preserve live-region roles set by custom ng-notfound-tmp / ng-loadingtext-tmp templates.
      if (existing === 'alert' || existing === 'status') {
        return;
      }
      if (existing !== 'option' && existing !== 'group') {
        el.setAttribute('role', 'option');
      }
      if (el.classList.contains('ng-option-disabled')) {
        el.setAttribute('aria-disabled', 'true');
      } else {
        el.removeAttribute('aria-disabled');
      }
      el.setAttribute('aria-selected', el.classList.contains('ng-option-selected') ? 'true' : 'false');
    });
  }

  private resolveAriaLabelledByText(labelledBy: string | null): string | null {
    if (!labelledBy?.trim()) {
      return null;
    }
    const parts = labelledBy
      .split(/\s+/)
      .map((id) => document.getElementById(id)?.textContent?.trim())
      .filter((t): t is string => !!t);
    return parts.length ? parts.join(' ') : null;
  }

  /** See class JSDoc — combobox must be on the element that receives keyboard focus. */
  private moveComboboxRoleToInput(input: HTMLInputElement): void {
    const ngInput = this.el.nativeElement.querySelector('.ng-input') as HTMLElement | null;
    if (!ngInput) {
      return;
    }
    if (ngInput.getAttribute('role') === 'combobox') {
      ngInput.removeAttribute('role');
      input.setAttribute('role', 'combobox');
    }
    if (ngInput.hasAttribute('aria-expanded')) {
      input.setAttribute('aria-expanded', ngInput.getAttribute('aria-expanded') || 'false');
      ngInput.removeAttribute('aria-expanded');
    }
    if (ngInput.hasAttribute('aria-owns')) {
      const owns = ngInput.getAttribute('aria-owns');
      if (owns) {
        input.setAttribute('aria-owns', owns);
      }
      ngInput.removeAttribute('aria-owns');
    }
    if (ngInput.hasAttribute('aria-haspopup')) {
      const haspopup = ngInput.getAttribute('aria-haspopup');
      if (haspopup) {
        input.setAttribute('aria-haspopup', haspopup);
      }
      ngInput.removeAttribute('aria-haspopup');
    }
  }

  private getFallbackComboboxAccessibleName(input: HTMLInputElement): string {
    const ph = input.placeholder?.trim();
    if (ph) {
      return ph;
    }
    const ghost = this.el.nativeElement.querySelector('.ng-placeholder');
    const text = ghost?.textContent?.trim();
    if (text) {
      return text;
    }
    return this.translate.instant('SELECT');
  }

  private fixClearButton(): void {
    const wrap = this.el.nativeElement.querySelector('.ng-clear-wrapper');
    if (!wrap) {
      return;
    }
    const fallbackLabel = wrap.getAttribute('title') || 'Clear';
    wrap.setAttribute('aria-label', this.ngSelectClearAriaLabel || fallbackLabel);
    wrap.querySelector('.ng-clear')?.removeAttribute('aria-hidden');
    // WCAG 2.1.1 — ensure clear control is keyboard operable.
    wrap.setAttribute('role', 'button');
    wrap.setAttribute('tabindex', '0');
    const wrapper = wrap as HTMLElement;
    if (!wrapper.dataset['a11yKeyboardBound']) {
      wrapper.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          wrapper.click();
        }
      });
      wrapper.dataset['a11yKeyboardBound'] = 'true';
    }
  }
}
